import React from "react";
import { Facebook, Instagram, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { log } from "../../lib/log";

const TikTokIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 13} height={p.size || 13} fill="currentColor" aria-hidden>
    <path d="M16.5 3c.3 2.2 1.6 3.6 3.9 3.8v3.1c-1.5 0-2.8-.4-3.9-1.1v6.4c0 3.2-2.6 5.8-5.8 5.8S4.9 18.4 4.9 15.2c0-3.3 2.8-5.9 6.2-5.7v3.2c-1.5-.3-3 .8-3 2.5 0 1.4 1.1 2.6 2.6 2.6s2.6-1.1 2.6-2.6V3h3.2z" />
  </svg>
);
const XIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 13} height={p.size || 13} fill="currentColor" aria-hidden>
    <path d="M17.5 3h3.1l-6.8 7.8L21.8 21h-6.3l-4.9-6.4L5 21H1.9l7.3-8.3L1.5 3H8l4.4 5.9L17.5 3zm-1.1 16.1h1.7L7.7 4.8H5.8l10.6 14.3z" />
  </svg>
);
const PinterestIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 13} height={p.size || 13} fill="currentColor" aria-hidden>
    <path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.3-5.4s-.3-.7-.3-1.6c0-1.5.9-2.7 2-2.7.9 0 1.4.7 1.4 1.6 0 1-.6 2.4-.9 3.7-.3 1.1.6 2 1.6 2 2 0 3.5-2.1 3.5-5.1 0-2.7-1.9-4.5-4.7-4.5-3.2 0-5.1 2.4-5.1 4.9 0 1 .4 2 .8 2.6.1.1.1.2.1.3l-.3 1.3c0 .2-.2.3-.4.2-1.4-.7-2.3-2.7-2.3-4.4 0-3.6 2.6-6.9 7.5-6.9 3.9 0 7 2.8 7 6.6 0 3.9-2.5 7.1-5.9 7.1-1.2 0-2.2-.6-2.6-1.3l-.7 2.7c-.3 1-1 2.2-1.4 3A10 10 0 1 0 12 2z" />
  </svg>
);
const ThreadsIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 13} height={p.size || 13} fill="currentColor" aria-hidden>
    <path d="M12.2 22c-2.9 0-5.1-1-6.6-2.9C4.3 17.4 3.6 15 3.6 12s.7-5.4 2-7.1C7.1 3 9.3 2 12.2 2c2.5 0 4.5.7 5.9 2.2 1 1 1.6 2.3 2 3.8l-2.1.6c-.3-1.1-.7-2-1.4-2.7-1-1-2.4-1.6-4.4-1.6-2.2 0-3.9.7-4.9 2.1-1 1.3-1.5 3.2-1.5 5.6s.5 4.3 1.5 5.6c1 1.4 2.7 2.1 4.9 2.1 2 0 3.4-.5 4.4-1.4.8-.8 1.2-1.8 1.2-2.9 0-.9-.3-1.7-.9-2.3-.4 1.2-1.2 2.1-2.3 2.7-.9.5-2 .8-3.1.7-1.2-.1-2.2-.5-2.9-1.2-.8-.8-1.2-1.8-1.1-2.9.1-1.1.6-2 1.5-2.7.9-.7 2.1-1 3.5-1 .7 0 1.4.1 2 .2-.1-.6-.3-1.1-.6-1.4-.4-.5-1.1-.8-2-.8-.9 0-1.7.3-2.3.9l-1.5-1.5c1-1 2.3-1.5 3.8-1.5 1.6 0 2.9.5 3.7 1.5.7.8 1 1.9 1.1 3.2 1.6.9 2.6 2.4 2.6 4.3 0 1.7-.6 3.2-1.8 4.4-1.5 1.5-3.5 2.1-6 2.1zm.6-9.1c-.9 0-1.6.2-2.1.5-.4.3-.6.7-.7 1.1 0 .4.1.8.4 1.1.3.3.8.5 1.5.5.7 0 1.3-.1 1.8-.4.8-.4 1.2-1.1 1.4-2.2-.7-.4-1.5-.6-2.3-.6z" />
  </svg>
);

// Social destinations. Facebook/X/WhatsApp/Telegram/Pinterest/Threads have web share intents that carry the
// postcard link; Instagram/TikTok don't accept web posts, so we hand them the PNG via the phone's share sheet
// (which lists both apps) and copy a ready caption — or on desktop save the PNG and open their upload page.
export function SocialShareRow({ shareUrl, imageUrl, caption, getPngFile }) {
  const open = (url) => window.open(url, "_blank", "noopener,width=680,height=640");
  const enc = encodeURIComponent;

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); return true; } catch (e) { log.debug("caption copy failed:", e); return false; }
  };

  const shareToApp = async (appName, fallbackUrl) => {
    const copied = await copyCaption();
    try {
      const file = await getPngFile();
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        toast.success(`${copied ? "Caption copied · " : ""}Pick ${appName} in the share sheet`);
        await navigator.share({ files: [file], title: "My StarGrid Blueprint", text: caption });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      log.debug(`${appName} file share unavailable:`, e?.name || e);
    }
    // Desktop / unsupported: save the PNG and open the upload page with the caption ready to paste.
    try {
      const file = await getPngFile();
      if (file) {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      }
    } catch (e) {
      log.warn("PNG save failed:", e);
    }
    toast.success(`Postcard saved${copied ? " · caption copied" : ""} — upload it in ${appName}`);
    open(fallbackUrl);
  };

  const items = [
    { id: "facebook", label: "Facebook", color: "#3B82F6", Icon: Facebook,
      run: () => open(`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(caption)}`) },
    { id: "instagram", label: "Instagram", color: "#C9744F", Icon: Instagram,
      run: () => shareToApp("Instagram", "https://www.instagram.com/") },
    { id: "tiktok", label: "TikTok", color: "#F5F5F5", Icon: TikTokIcon,
      run: () => shareToApp("TikTok", "https://www.tiktok.com/upload") },
    { id: "whatsapp", label: "WhatsApp", color: "#9CBF8E", Icon: MessageCircle,
      run: () => open(`https://wa.me/?text=${enc(caption)}`) },
    { id: "twitter", label: "X", color: "#38BDF8", Icon: XIcon,
      run: () => open(`https://twitter.com/intent/tweet?text=${enc(caption.replace(shareUrl, "").trim())}&url=${enc(shareUrl)}`) },
    { id: "threads", label: "Threads", color: "#D4D4D8", Icon: ThreadsIcon,
      run: () => open(`https://www.threads.net/intent/post?text=${enc(caption)}`) },
    { id: "telegram", label: "Telegram", color: "#60A5FA", Icon: Send,
      run: () => open(`https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(caption.replace(shareUrl, "").trim())}`) },
    { id: "pinterest", label: "Pinterest", color: "#F87171", Icon: PinterestIcon,
      run: () => open(`https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(imageUrl)}&description=${enc(caption)}`) },
  ];

  return (
    <div className="mt-4" data-testid="postcard-social-row">
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#A89C98] mb-2">Post to</div>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        {items.map(({ id, label, color, Icon, run }) => (
          <button
            key={id}
            type="button"
            onClick={run}
            data-testid={`postcard-share-${id}`}
            aria-label={`Share to ${label}`}
            className="group flex flex-col items-center gap-1.5 py-2 rounded-xl border border-white/10 hover:border-white/30 bg-white/[0.03] hover:bg-white/[0.07] transition-colors"
          >
            <span className="w-9 h-9 rounded-full flex items-center justify-center border transition-transform group-hover:scale-110" style={{ color, borderColor: `${color}66`, background: `${color}14` }}>
              <Icon size={15} />
            </span>
            <span className="text-[9px] uppercase tracking-[0.15em] text-[#A89C98] group-hover:text-white">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
